/**
 * Client-side tools for AI agent threads.
 *
 * Each tool lives in its own file and exports a {@link ClientTool}.
 * To add a new client tool:
 *
 * 1. Create a new file in this directory (e.g., `myTool.ts`)
 * 2. Define an input type and implement execute
 * 3. Export a `ClientTool` (directly or via {@link createClientTool})
 * 4. Import and register it in the `TOOL_REGISTRY` map below
 *
 * See `navigate.ts` for a full example with dependencies and parameterized routing.
 */
import { tool as clientCreateAgentTool } from "./clientCreateAgent";
import { createClientTool } from "./ClientTools";
import type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
  ConfirmationContentProps,
} from "./ClientTools";
import { tool as createEventsTool } from "./createEvents";
import { tool as navigateTool } from "./navigate";
import {
  openFileTool,
  openPanelTool,
  searchTopicTool,
  seekTool,
} from "./visualize";

export { createClientTool };
export type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
  ConfirmationContentProps,
};

/**
 * Registry of all client tools, keyed by tool name.
 *
 * Each tool is registered as a {@link ClientTool}. Use
 * {@link createClientTool} to build a module from a typed config.
 *
 * Add new tools here after creating their module file.
 */
const TOOL_REGISTRY: ReadonlyMap<string, ClientTool> = new Map([
  [navigateTool.spec.name, navigateTool],
  [createEventsTool.spec.name, createEventsTool],
  [clientCreateAgentTool.spec.name, clientCreateAgentTool],
  [openPanelTool.spec.name, openPanelTool],
  [searchTopicTool.spec.name, searchTopicTool],
  [openFileTool.spec.name, openFileTool],
  [seekTool.spec.name, seekTool],
]);

/**
 * All registered tool modules, for use by the settings modal and
 * approval resolution logic.
 */
export const CLIENT_TOOL_MODULES: readonly ClientTool[] = [
  ...TOOL_REGISTRY.values(),
];

/**
 * All client-side tool specs, sent to the backend with every thread request
 * so the LLM knows these tools are available.
 */
export const BROWSER_CLIENT_TOOLS = CLIENT_TOOL_MODULES.map(
  (tool) => tool.spec,
);

/**
 * Look up a client tool module by name.
 *
 * Returns undefined for unknown tools.
 */
export function getClientTool(toolName: string): ClientTool | undefined {
  return TOOL_REGISTRY.get(toolName);
}

/**
 * Execute a client-side tool by name with the given input parameters.
 *
 * Looks up the tool in the registry and calls its `execute` function.
 * Returns an error result for unknown tools.
 */
export async function executeClientTool(
  toolName: string,
  params: Record<string, unknown>,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  const tool = TOOL_REGISTRY.get(toolName);
  if (!tool) {
    return {
      response: { error: `Unknown client tool: ${toolName}` },
      success: false,
    };
  }
  return tool.execute(params, deps);
}
